import InlineCalendar from "PFComponents/calendar/inline_calendar";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import Toggle from "PFComponents/toggle/toggle";
import useForceUpdate from "PFCore/helpers/use_force_update";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./overwrites.module.scss";
import ResetButton from "./reset_button";

const Position = ({ position, exportableProfile, onModified, timesModified }) => {
  const { t } = useTranslation();
  const forceUpdate = useForceUpdate();

  const { formatISODateTime } = useDateFormatter();

  const [hasChanged, setHasChanged] = useState(false);

  useEffect(() => {
    if (timesModified === 0) {
      setHasChanged(false);
    }
  }, [timesModified]);

  useEffect(() => {
    onModified((prev) => prev + 1);
  }, [hasChanged]);

  useEffect(() => {
    setHasChanged(exportableProfile.hasChangedPosition(position.id));
  }, [exportableProfile.hasChangedPosition(position.id)]);

  const { startDate, endDate } = position;

  return (
    <tr className={css.position}>
      <td>
        <Toggle
          checked={position.enabled}
          onChange={(checked) => {
            exportableProfile.updatePosition(position.id, { enabled: checked });
            setHasChanged(exportableProfile.hasChangedPosition(position.id));
            forceUpdate();
          }}
        />
      </td>
      <td style={{ width: "90%" }}>
        <InputFieldSet
          label={t("export.edit.jobTitle")}
          value={position.jobTitle || ""}
          onChange={(value) => {
            exportableProfile.updatePosition(position.id, { jobTitle: value });
            setHasChanged(exportableProfile.hasChangedPosition(position.id));
            forceUpdate();
          }}
        />
        <br />
        <InputFieldSet
          label={t("export.edit.company")}
          value={position.companyName || ""}
          onChange={(value) => {
            exportableProfile.updatePosition(position.id, { companyName: value });
            setHasChanged(exportableProfile.hasChangedPosition(position.id));
            forceUpdate();
          }}
        />
        <br />
        <InputFieldSet
          label={t("export.edit.summary")}
          inputType="textarea"
          value={position.summary || ""}
          onChange={(value) => {
            exportableProfile.updatePosition(position.id, { summary: value });
            setHasChanged(exportableProfile.hasChangedPosition(position.id));
            forceUpdate();
          }}
        />
        <br />
        <div className={css.calendarsWrap}>
          <InlineCalendar
            style={{ flex: 1 }}
            label={t("export.edit.startDate")}
            selectedDate={startDate ? formatISODateTime(startDate) : null}
            handleChange={(date) => {
              exportableProfile.updatePosition(position.id, { startDate: formatISODateTime(date) });
              setHasChanged(exportableProfile.hasChangedPosition(position.id));
              forceUpdate();
            }}
          />

          <span className={css.calendarSpace} />
          <span className={css.calendarVSpace} />

          {!position.current && (
            <InlineCalendar
              style={{ flex: 1 }}
              label={t("export.edit.endDate")}
              selectedDate={endDate ? formatISODateTime(endDate) : null}
              handleChange={(date) => {
                exportableProfile.updatePosition(position.id, { endDate: formatISODateTime(date) });
                setHasChanged(exportableProfile.hasChangedPosition(position.id));
                forceUpdate();
              }}
            />
          )}

          <span className={css.calendarVSpace} />

          <Toggle
            inline
            label={t("export.edit.currentJob")}
            checked={position.current}
            onChange={(checked) => {
              position.current = checked;
              exportableProfile.updatePosition(position.id, { current: checked });
              setHasChanged(exportableProfile.hasChangedPosition(position.id));
              forceUpdate();
            }}
          />
        </div>
      </td>
      <td className={css.resetLabel}>
        {hasChanged && (
          <ResetButton
            onClick={() => {
              exportableProfile.resetPosition(position.id);
              setHasChanged(false);
              onModified((prev) => prev - 1);
              forceUpdate();
            }}
          />
        )}
      </td>
    </tr>
  );
};

Position.propTypes = {
  exportableProfile: PropTypes.object,
  position: PropTypes.shape({
    summary: PropTypes.string,
    current: PropTypes.bool,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    id: PropTypes.number,
    jobTitle: PropTypes.string,
    companyName: PropTypes.string,
    enabled: PropTypes.bool
  }),
  onModified: PropTypes.func,
  timesModified: PropTypes.number
};

export default Position;
